import React, { FC, Fragment, useContext, useState } from 'react';
import { Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import AddMillingJobDialog from './add-milling-job-dialog.component';
import { useMutation, useQuery } from '@apollo/client';
import {
    deleteMillingJobTemplate,
    millingJobsTemplate,
    millingJobTypesTemplate,
    redoMillingJobTemplate,
} from 'apollo/templates/milling';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { MillingJob, MillingJobCopyReason } from 'models/milling';
import MillingJobRow from './milling-job-row.componenet';
import LinearProgressBarWithLabel from 'components/shared/linear-progress-bar-with-label/linear-progress-bar-with-label.component';
import axios, { AxiosProgressEvent } from 'axios';
import Cookies from 'js-cookie';
import { CONSTANTS } from 'config/constants';
import InfoText from 'components/shared/info-text.component';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { OrderDetailsContext } from 'pages/order-details/order-details-context';

type MillingJobsForOrderProps = {
    orderDbId: string;
    orderId: string;
    internalOrderId: string;
};
const MillingJobsForOrder: FC<MillingJobsForOrderProps> = ({
    orderDbId,
    orderId,
    internalOrderId,
}: MillingJobsForOrderProps) => {
    const { t: translate } = useTranslation(['common', 'milling-jobs', 'orders']);
    const [addMillingJobOpen, setAddMillingJobOpen] = useState(false);
    const [disabledAddButton, setDisabledAddButton] = useState(false);
    const [progress, setProgress] = useState<number>(0);
    const [uploading, setUploading] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);
    // The order is already in the Apollo cache from the page's own query, so the dialog's
    // read-only shade panel (DD-3830) costs no extra request.
    const { order } = useContext(OrderDetailsContext);

    const {
        data: { millingJobs } = {},
        loading,
        refetch,
    } = useQuery(millingJobsTemplate, {
        variables: { orderId: orderDbId },
        onError(error) {
            showBoundary(error);
        },
    });

    const { data: { millingJobTypes } = {} } = useQuery(millingJobTypesTemplate, {
        onError(error) {
            showBoundary(error);
        },
    });

    const [deleteMillingJob] = useMutation(deleteMillingJobTemplate, {
        onCompleted: () => {
            refetch();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [redoMillingJob] = useMutation(redoMillingJobTemplate, {
        onCompleted: () => {
            refetch();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleAddMillingJob = async (job: Partial<MillingJob>) => {
        try {
            setAddMillingJobOpen(false);
            setDisabledAddButton(true);
            setUploading(true);
            const { files, ...otherData } = { ...job, order: orderDbId };
            const formData = new FormData();
            for (const file of files || []) {
                formData.append('files', file, file.name);
            }
            Object.keys(otherData).forEach((key) => {
                formData.append(key, otherData[key as keyof typeof otherData] as string);
            });
            const response = await axios.post(`${CONSTANTS.DOC_API_URI}/upload/milling-job`, formData, {
                headers: {
                    Authorization: Cookies.get(CONSTANTS.TOKEN_KEY),
                },
                onUploadProgress: (ev: AxiosProgressEvent) => {
                    setProgress((ev.loaded / (ev.total || 100)) * 100);
                },
            });
            if (response.status === 200) {
                refetch();
            }
        } catch (error) {
            // No response = network unreachable; 5xx = denthub-documents reached but its upstream (e.g. S3/storage) failed due to connectivity.
            // Both cases surface as upload failures to the user rather than crashing the app via showBoundary.
            const isUploadFailure = axios.isAxiosError(error) && (!error.response || error.response.status >= 500);
            if (isUploadFailure) {
                setAppToasterContent({
                    isOpen: true,
                    severity: 'error',
                    message: translate('milling-jobs:UPLOAD_NETWORK_ERROR'),
                });
            } else {
                showBoundary(error);
            }
        } finally {
            setProgress(0);
            setUploading(false);
            setDisabledAddButton(false);
        }
    };

    const handleDeleteClick = async (jobId?: string) => {
        if (jobId) {
            await deleteMillingJob({ variables: { millingJobId: jobId } });
        }
    };

    const handleCreateWarranty = async (jobId?: string) => {
        if (jobId) {
            await redoMillingJob({ variables: { millingJobId: jobId, reason: MillingJobCopyReason.WARRANTY } });
        }
    };

    return (
        <Fragment>
            <Box mb={1} display="flex">
                <Box>
                    <Button
                        disabled={disabledAddButton}
                        fullWidth
                        onClick={() => {
                            setAddMillingJobOpen(true);
                        }}
                        startIcon={<AddIcon />}
                    >
                        {translate('milling-jobs:ADD_MILLING_JOB')}
                    </Button>
                </Box>
            </Box>
            {!loading ? (
                <Fragment>
                    {uploading ? <LinearProgressBarWithLabel progress={progress} /> : undefined}
                    {millingJobs.length > 0 ? (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{translate('milling-jobs:JOB_GROUP')}</TableCell>
                                        <TableCell>{translate('common:FILENAME')}</TableCell>
                                        <TableCell>{translate('orders:INTERNAL_ORDER_ID')}</TableCell>
                                        <TableCell>{translate('milling-jobs:DEADLINE')}</TableCell>
                                        <TableCell>{translate('common:CREATED')}</TableCell>
                                        <TableCell></TableCell>
                                        <TableCell sx={{ width: 150 }}></TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {millingJobs.map((job: MillingJob) => (
                                        <MillingJobRow
                                            key={job._id}
                                            job={job}
                                            onDeleteClick={handleDeleteClick}
                                            onCreateWarrantyClick={handleCreateWarranty}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <InfoText text={translate('milling-jobs:NO_MILLING_JOBS')} />
                    )}
                </Fragment>
            ) : undefined}

            <AddMillingJobDialog
                millingJobTypes={millingJobTypes}
                prePopulateFilename={internalOrderId ? `${orderId}-${internalOrderId}` : orderId}
                restorations={order?.restorations ?? []}
                open={addMillingJobOpen}
                onAdd={handleAddMillingJob}
                onClose={() => setAddMillingJobOpen(false)}
            />
        </Fragment>
    );
};

export default MillingJobsForOrder;
